import { useEffect, useState } from "react";

import InstructorsShowcase from "@/my_components/_HomePage/Home-Sections/InstructorsShowcase";
import { getPlatformStats } from "@/services/courseService";

/**
 * The dedicated "/instructors" page's content -- every real YouTube creator credited anywhere
 * on the platform, ranked by lesson-credit count (see
 * CourseController::PublicInstructorsFetcher). The homepage only ever shows a capped top-15
 * carousel (see InstructorsShowcase's `limit` prop); this page is where "See all instructors"
 * leads to, so it uses InstructorsShowcase's `paginated` grid mode instead, which loads every
 * instructor a page (12) at a time via infinite scroll -- same load-more pattern as
 * my_components/_Courses/CourseRoom.js uses for the course catalog.
 *
 * Hero: real platform stats (students/courses from the same endpoint the homepage hero uses,
 * instructor count straight off the list this page itself renders -- never a separate, possibly
 * stale number) plus a by-name search bar, wired straight into PublicInstructorsFetcher's
 * `?search=` param via InstructorsShowcase's `search` prop.
 */
const InstructorsRoom = () => {
  const [stats, setStats] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    getPlatformStats().then((res) => setStats(res.data)).catch(() => {});
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };

  return (
    <div className="rbt-instructor-area rbt-section-gapBottom">
      <div className="rbt-instructors-hero">
        <div className="rbt-instructors-hero__glow" aria-hidden="true"></div>
        <div className="container">
          <div className="rbt-instructors-hero__inner">
            <span className="subtitle bg-primary-opacity">Who&apos;s Teaching</span>
            <h1 className="rbt-instructors-hero__title">Meet every instructor on SmartTech Academy</h1>
            <p className="rbt-instructors-hero__description">
              Every real engineer, designer and YouTube creator whose footage is clipped into a SmartTech Academy lesson, ranked by how much
              of the catalog they&apos;re behind.
            </p>

            <form className="rbt-instructors-hero__search" onSubmit={handleSearchSubmit}>
              <i className="feather-search"></i>
              <input
                type="text"
                placeholder="Search instructors by name…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              {search && (
                <button
                  type="button"
                  className="rbt-instructors-hero__search-clear"
                  onClick={() => {
                    setSearchInput("");
                    setSearch("");
                  }}
                  aria-label="Clear search"
                >
                  <i className="feather-x"></i>
                </button>
              )}
              <button type="submit">Search</button>
            </form>

            {stats && (
              <div className="rbt-instructors-hero__stats">
                <div className="rbt-instructors-hero__stat">
                  <strong>{stats.total_instructors}</strong>
                  <span>Real instructors</span>
                </div>
                <div className="rbt-instructors-hero__stat">
                  <strong>{stats.total_courses}+</strong>
                  <span>Courses taught</span>
                </div>
                <div className="rbt-instructors-hero__stat">
                  <strong>{stats.total_students}+</strong>
                  <span>Students reached</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="container">
        {search && (
          <p className="rbt-instructors-hero__search-note">
            Showing results for <strong>&quot;{search}&quot;</strong>
          </p>
        )}
        <InstructorsShowcase layout="grid" paginated perPage={12} search={search} />
      </div>
    </div>
  );
};

export default InstructorsRoom;
